import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOrgTimezone, formatLocalDateInTz } from '../../utils/timezone';
import PageHeader from '../../components/PageHeader';
import { ShoppingCart, Package, Calendar, Truck, TreePine } from 'lucide-react';
import nurseryService from '../../services/nursery';
import type { NurseryOrder, NurseryOrderStatus } from '../../types/nursery';
import { toNumOr } from '@/utils/numeric';

const STATUS_COLORS: Record<NurseryOrderStatus, string> = {
  draft:                'bg-neutral-100 text-neutral-700',
  confirmed:            'bg-blue-100 text-blue-700',
  allocated:            'bg-indigo-100 text-indigo-700',
  partially_allocated:  'bg-amber-100 text-amber-700',
  picking:              'bg-yellow-100 text-yellow-700',
  picked:               'bg-teal-100 text-teal-700',
  shipped:              'bg-cyan-100 text-cyan-700',
  invoiced:             'bg-green-100 text-green-700',
  completed:            'bg-green-100 text-green-800',
  cancelled:            'bg-red-100 text-red-700',
};

function CustomerOrdersDashboard() {
  const timezone = useOrgTimezone();
  const { t } = useTranslation('nursery', { keyPrefix: 'CustomerOrdersDashboard' });
  const [orders, setOrders] = useState<NurseryOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await nurseryService.getMyOrders();
        if (res.success) {
          setOrders(res.data || []);
        }
      } catch (e: any) {
        setError(e?.message || t('loadFailed'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [t]);

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        icon={<ShoppingCart className="h-6 w-6 text-white" />}
      />
      <div className="max-w-7xl mx-auto px-4 py-6">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 mb-6">
            {error}
          </div>
        )}

        {loading ? (
          <div className="space-y-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-neutral-100 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-16 text-neutral-400">
            <ShoppingCart className="h-12 w-12 mx-auto mb-3 opacity-40" />
            <p className="font-medium text-neutral-600">{t('empty')}</p>
            <p className="text-sm mt-1">{t('emptyHint')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map(order => {
              const statusColor = STATUS_COLORS[order.status] || 'bg-neutral-100 text-neutral-700';
              return (
                <Link
                  key={order.id}
                  to={`/nursery/my-orders/${order.id}`}
                  className="block bg-white border border-neutral-200 rounded-xl p-5 hover:border-primary-300 hover:shadow-sm transition-all"
                >
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-neutral-800">{order.order_number}</span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${statusColor}`}>
                          {t(`status.${order.status}`, { defaultValue: order.status.replace(/_/g, ' ') })}
                        </span>
                      </div>

                      <div className="flex items-center gap-4 text-xs text-neutral-500">
                        <span className="flex items-center gap-1">
                          <TreePine className="h-3.5 w-3.5" />
                          {order.nursery_name || t('nurseryFallback', { id: order.nursery_org_id })}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          {formatLocalDateInTz(order.order_date, timezone)}
                        </span>
                        {order.estimated_delivery_date && (
                          <span className="flex items-center gap-1">
                            <Truck className="h-3.5 w-3.5" />
                            {t('delivery', { date: formatLocalDateInTz(order.estimated_delivery_date, timezone) })}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="text-right">
                      <div className="flex items-center gap-1 text-sm font-semibold text-neutral-800">
                        <Package className="h-4 w-4 text-neutral-400" />
                        {t('trees', { count: order.total_trees, n: order.total_trees.toLocaleString() })}
                      </div>
                      {toNumOr(order.total_value) > 0 && (
                        <p className="text-xs text-neutral-500 mt-0.5">
                          ${toNumOr(order.total_value).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </p>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

export default CustomerOrdersDashboard;
